import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { faAddressCard, faBan, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Discipline, DisciplineNew, DisciplineType, } from '../../models/models.lib';
import { DisciplineService } from '../discipline.service';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { setTitle } from '../../common/utils.lib';
import { DisciplineEditComponent } from '../discipline-edit.component/discipline-edit.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { ToastrService } from 'ngx-toastr';
import {
  DisciplineSubscaleListComponent
} from '../../discipline-subscale/discipline-subscale-list.component/discipline-subscale-list.component';
import { DisciplineSubscaleListWin } from '../../discipline-subscale/discipline-subscale-list-win/discipline-subscale-list-win';

const { DISCIPLINE } = AppEnities;

@Component({
  selector: 'casi-discipline-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './discipline-list.component.html',
  styleUrl: './discipline-list.component.scss'
})
export class DisciplineListComponent implements OnInit, OnDestroy {
  public view: Observable<Discipline[]> = new Observable<Discipline[]>();
  public rawData: Discipline[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Discipline[] = filterBy(this.rawData, this.filter);

  public currentItem: DisciplineType = DisciplineNew.create();

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public editWinOpen: Boolean = false;

  public formMode: FormModes = FormModes.NOTSET;

  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: DisciplineService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private windowService: WindowService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan
    );
  }

  public sort: SortDescriptor[] = [
    { field: "discipline_name", dir: "asc" },
    // { field: "createdAt", dir: "desc" },
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_destroy } = this.me.allICanDo(DISCIPLINE);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canDestroy = can_destroy;
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }


  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
    // this.skip = event.skip;
    // this.loadItems();
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = Discipline.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onAdd(): void {
    this.logger.debug("onAdd...");
    this.currentItem = DisciplineNew.create();
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Discipline.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  public onSubscaleClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onSubscaleClick: ", dataItem);
    let item = Discipline.create(dataItem);
    this.logger.debug("onSubscaleClick:currentItem ", item);
    this.openSubscaleDialog(item, titleBar);
  }

  private openSubscaleDialog(dataItem: Discipline, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openSubscaleDialog...");
    this.closeWindowRef();
    this.showTitle = setTitle(this.formMode, 'MANAGE DISCIPLINE SUBSCALES');

    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      content: DisciplineSubscaleListWin, // DisciplineSubscaleListComponent,
      minWidth: 320,
      width: 1000,
    });
    // const dialogRef = this.dialogService.open({
    //   content: DisciplineSubscaleListComponent,
    //   minWidth: 320,
    //   width: 1000,
    // });

    this.setWinInstanceInputs(dataItem);
    // const contentRef = dialogRef.content.instance as DisciplineSubscaleListComponent;
    // contentRef.title = "MANAGE DISCIPLINE SUBSCALES";
    // contentRef.discipline = dataItem;

    // this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
    //   this.logger.debug('openSubscaleDialog:formData: ', formData);
    //   // this.save(formData.value);
    // });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openSubscaleDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('openSubscaleDialog:onCancel');
      this.closeWindowRef();
    });

  }

  private setWinInstanceInputs(dataItem: Discipline): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      // inputs.title = 'MANAGE DISCIPLINE SUBSCALES';
      inputs.discipline = dataItem;
      this.logger.debug('setWinInstanceInputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: DisciplineType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: DisciplineEditComponent,
      minWidth: 320,
      width: 450,
    });

    const contentRef = dialogRef.content.instance as DisciplineEditComponent;
    contentRef.title = setTitle(mode, 'DISCIPLINE');
    contentRef.discipline = dataItem;
    contentRef.mode = mode;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.disciplineForm.value);
        this.handleClose(contentRef.disciplineForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    let dup: boolean = false;
    if (data) {
      let inUse = this.rawData.find(r => r.discipline_name === data.discipline_name);
      this.logger.debug('handleClose...inUse', inUse);
      if (inUse) {
        if (mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleClose: discipline: ${data.discipline_name} already in use `);
        this.openErrorDailog("Duplicate Discipline Name", "That Discipline already exists!", "Close and Retry")
      } else
        this.saveDiscipline(data, mode);
    }
  }

  private saveDiscipline(discipline: any, mode: FormModes): void {
    this.logger.debug('saveDiscipline...', discipline, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(discipline.id, discipline)
        .then(resp => {
          this.logger.debug('saveDiscipline:update:resp: ', resp);
          this.notify("Discipline Updated");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveDiscipline:update:err: ', err);
          this.notify("Error Updating Discipline", true);
        });
    } else {
      this.dataService.add(discipline)
        .then(resp => {
          this.logger.debug('saveDiscipline:add:resp: ', resp);
          this.notify("Discipline Added");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveDiscipline:add:err: ', err);
          this.notify("Error Creating Discipline", true);
        });
    }
  }

  private saveActive(discipline: any): void {
    this.logger.debug('saveDiscipline...', discipline);
    this.dataService.update(discipline.id, discipline)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify(discipline.active ? "Discipline Activated" : "Discipline Deactivated");
        this.closeWindowRef();
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Discipline", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}
